import * as React from "react";
import { Card, cn } from "@aisquare/ui";
import { ArrowRightIcon } from "lucide-react";
import pkg from "../../../package.json";
import {
  DEMOS,
  LATEST_VERSION,
  LAYERS,
  layerMetaFor,
  NAV,
  NEW_COUNT,
  type Layer,
  type NavLayer,
} from "./registry";
import { hrefForComponent, hrefForLayer, navigate } from "./router";
import { CopyButton, Eyebrow, LayerBadge, NewBadge } from "./chrome";

/**
 * The landing page and the three layer index pages.
 *
 * Both exist because a 118-component library needs somewhere to answer "what is
 * in here, and where does this thing live" before you start clicking names in a
 * sidebar. The overview is the whole surface at a glance; a layer page is one
 * architectural tier of it, which is the unit design QA actually reviews in.
 */

/** Faint navy/accent wash + hairline grid behind the hero. */
function HeroBackdrop() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-80 overflow-hidden">
      <div className="pg-grid pg-fade-out absolute inset-0 opacity-60 dark:opacity-40" />
      <div className="bg-mesh-aurora absolute -top-40 start-1/4 size-[38rem] opacity-[0.07] blur-3xl" />
    </div>
  );
}

function StatCell({ value, label }: { value: React.ReactNode; label: string }) {
  return (
    <div>
      <div className="font-mono text-title tabular-nums text-foreground">{value}</div>
      <div className="mt-0.5 font-mono text-eyebrow uppercase text-muted-foreground">{label}</div>
    </div>
  );
}

/** A component's tile in an index grid. */
function ComponentTile({ id, title, since }: { id: string; title: string; since: string }) {
  return (
    <a
      href={hrefForComponent(id)}
      className={cn(
        "group flex items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 py-2.5",
        "transition-colors duration-180 ease-smooth-out hover:border-accent/40 hover:bg-accent/5",
      )}
    >
      <span className="min-w-0 truncate text-label text-foreground">{title}</span>
      <span className="flex shrink-0 items-center gap-1.5">
        <NewBadge since={since} />
        <ArrowRightIcon className="size-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 rtl:rotate-180" />
      </span>
    </a>
  );
}

/** Every component in a layer, grouped by its functional category. */
function LayerCatalog({ layer }: { layer: NavLayer }) {
  return (
    <div className="space-y-7">
      {layer.categories.map((c) => (
        <div key={c.category}>
          <Eyebrow className="mb-2.5">
            {c.category}
            <span className="ms-2 opacity-50">{c.entries.length}</span>
          </Eyebrow>
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {c.entries.map((e) => (
              <ComponentTile key={e.id} id={e.id} title={e.title} since={e.since} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function Overview() {
  const install = `pnpm add ${pkg.name}`;
  const newest = DEMOS.filter((d) => d.since === LATEST_VERSION);

  return (
    <div className="relative">
      <HeroBackdrop />

      <div className="relative pb-4 pt-10">
        <Eyebrow className="mb-3">AISquare design system · v{pkg.version}</Eyebrow>
        {/* The editorial serif at display size — the brand's hero voice. */}
        <h1 className="max-w-[22ch] font-display text-display">
          <span className="pg-ink-gradient">One package,</span>
          <br />
          three layers.
        </h1>
        <p className="mt-5 max-w-[62ch] text-body-lg text-muted-foreground">
          Every primitive exported from{" "}
          <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-body text-foreground">
            {pkg.name}
          </code>
          , live, in both themes and both directions. Pick a component from the rail to open its
          page — preview, per-example source, and the import to paste.
        </p>

        <div className="mt-7 flex max-w-md items-center gap-2 rounded-lg border border-border bg-card ps-3 pe-1.5 py-2">
          <code dir="ltr" className="min-w-0 flex-1 truncate font-mono text-label text-foreground">
            <span className="select-none text-muted-foreground">$ </span>
            {install}
          </code>
          <CopyButton value={install} label="Copy install command" />
        </div>

        <div className="mt-9 flex flex-wrap gap-x-10 gap-y-5 border-t border-border pt-6">
          <StatCell value={DEMOS.length} label="components" />
          <StatCell value={LAYERS.length} label="layers" />
          <StatCell value={NEW_COUNT} label={`new in ${LATEST_VERSION}`} />
          <StatCell value={`v${pkg.version}`} label="version" />
        </div>
      </div>

      <section className="mt-14">
        <Eyebrow className="mb-4">The layers</Eyebrow>
        <div className="grid gap-4 lg:grid-cols-3">
          {NAV.map((layer) => (
            // Clickable card, so it wears the Onyx hex notch + clip-safe
            // elevation + rim keyline — the library's own "this is pressable"
            // cue, applied by the rule rather than re-styled here.
            <Card
              key={layer.meta.id}
              interactive
              hover="glow"
              onClick={() => navigate(hrefForLayer(layer.meta.id))}
              aria-label={`${layer.meta.label} — ${layer.total} components`}
              className="p-5"
            >
              <div className="flex items-center justify-between gap-2">
                <LayerBadge meta={layer.meta} />
                <span className="font-mono text-caption tabular-nums text-muted-foreground">
                  {layer.total}
                </span>
              </div>
              <h2 className="mt-3 text-title text-foreground">{layer.meta.label}</h2>
              <p className="mt-2 text-caption leading-relaxed text-muted-foreground">
                {layer.meta.blurb}
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-label text-accent">
                Browse
                <ArrowRightIcon className="size-3.5 rtl:rotate-180" />
              </span>
            </Card>
          ))}
        </div>
      </section>

      {newest.length > 0 ? (
        <section className="mt-14">
          <Eyebrow className="mb-4">
            New in {LATEST_VERSION}
            <span className="ms-2 opacity-50">{newest.length}</span>
          </Eyebrow>
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {newest.map((e) => (
              <ComponentTile key={e.id} id={e.id} title={e.title} since={e.since} />
            ))}
          </div>
        </section>
      ) : null}

      <section className="mt-14 pb-16">
        <Eyebrow className="mb-4">Everything</Eyebrow>
        <div className="space-y-10">
          {NAV.map((layer) => (
            <div key={layer.meta.id}>
              <a
                href={hrefForLayer(layer.meta.id)}
                className="mb-4 inline-flex items-center gap-2 text-title text-foreground transition-colors hover:text-accent"
              >
                {layer.meta.label}
                <ArrowRightIcon className="size-4 rtl:rotate-180" />
              </a>
              <LayerCatalog layer={layer} />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function LayerIndex({ id }: { id: Layer }) {
  const meta = layerMetaFor(id);
  const layer = NAV.find((l) => l.meta.id === id);
  if (!layer) return null;

  return (
    <div className="pb-16 pt-8">
      <header className="mb-10 max-w-[70ch]">
        <Eyebrow className="mb-2">Layer</Eyebrow>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-display text-heading text-foreground">{meta.label}</h1>
          <LayerBadge meta={meta} />
          <span className="font-mono text-caption tabular-nums text-muted-foreground">
            {layer.total} components
          </span>
        </div>
        <p className="mt-3 text-body text-muted-foreground">{meta.blurb}</p>
        <div className="mt-4 flex max-w-md items-center gap-2 rounded-lg border border-border bg-card ps-3 pe-1.5 py-1.5">
          <code
            dir="ltr"
            className="min-w-0 flex-1 truncate font-mono text-caption text-foreground/80"
          >
            {`import { … } from "${meta.importPath}";`}
          </code>
          <CopyButton value={meta.importPath} label="Copy import path" />
        </div>
      </header>

      <LayerCatalog layer={layer} />
    </div>
  );
}
